import { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import ErrorBoundary from "./ErrorBoundary";
import LoginPage from "../pages/auth/LoginPage";
import EmployeeLimitedAccessPage from "../pages/employee/EmployeeLimitedAccessPage";
import DesktopLoginPage from "../pages/desktop/DesktopLoginPage";
import DesktopPage from "../pages/desktop/DesktopPage";
import { getDesktopValue, removeDesktopValue, setDesktopValue } from "../utils/desktopStore";
import { parseApiJson } from "../utils/parseApiJson";
import { API_URL } from "../pages/dashboard/constants";

const DashboardPage = lazy(() => import("../pages/dashboard/DashboardPage"));

function isDesktopRuntime() {
  const ua = navigator.userAgent || "";
  return ua.toLowerCase().includes("electron");
}

function isFullPortalRole(role) {
  const normalizedRole = String(role || "").toLowerCase();
  return normalizedRole === "co-owner" || normalizedRole === "admin" || normalizedRole === "manager";
}

function shouldClearPersistedSession(response) {
  return response.status === 401 || response.status === 403;
}

function App() {
  const [session, setSession] = useState(() => {
    try {
      const saved = localStorage.getItem("workivity-portal-session");
      return saved ? JSON.parse(saved) : null;
    } catch (_error) {
      return null;
    }
  });
  const [desktopSession, setDesktopSession] = useState(() => {
    return null;
  });

  const isDesktop = useMemo(() => isDesktopRuntime(), []);
  const [desktopReady, setDesktopReady] = useState(!isDesktop);
  const notifiedDesktopTokenRef = useRef(null);

  const notifyDesktopLogin = useCallback(async function notifyDesktopLogin(payload) {
    try {
      if (!isDesktop || !window.desktopMonitor?.onLoginSuccess) {
        return;
      }
      await window.desktopMonitor.onLoginSuccess({
        email: payload?.user?.email || null,
        id: payload?.user?.id || payload?.user?._id || null,
        // The main process owns the socket, so it needs the device tokens the
        // login returned. Without these the handshake has nothing to present
        // and the server answers AUTH_REQUIRED.
        refreshToken: payload?.refreshToken || null,
        accessToken: payload?.accessToken || null,
        accessExpiresIn: payload?.accessExpiresIn || null,
        deviceId: payload?.deviceId || null,
      });
    } catch (_error) {
      // Monitoring initialization should not block user login.
    }
  }, [isDesktop]);

  function handleLoginSuccess(payload) {
    const nextSession = {
      token: payload.token,
      user: payload.user,
    };
    setSession(nextSession);
    localStorage.setItem("workivity-portal-session", JSON.stringify(nextSession));
  }

  function handleLogout() {
    setSession(null);
    localStorage.removeItem("workivity-portal-session");
  }

  // A password change signs every other device out and returns a fresh token
  // for this one, so the stored session has to be swapped in place.
  function handleSessionTokenRefresh(token) {
    if (!token) {
      return;
    }
    setSession((previous) => {
      if (!previous) {
        return previous;
      }
      const nextSession = { ...previous, token };
      try {
        localStorage.setItem("workivity-portal-session", JSON.stringify(nextSession));
      } catch (_error) {
        /* ignore storage failures; the in-memory session still works */
      }
      return nextSession;
    });
  }

  async function handleDesktopLoginSuccess(payload) {
    const nextSession = {
      token: payload.token,
      user: payload.user,
    };
    setDesktopSession(nextSession);
    await setDesktopValue("workivity-desktop-session", nextSession);
    // Hands the device tokens to the main process, which is what actually
    // connects the socket.
    await notifyDesktopLogin(payload);
  }

  /** Drops every local trace of the desktop session, legacy keys included. */
  async function clearDesktopSessionLocally() {
    await removeDesktopValue("workivity-desktop-session");
    await removeDesktopValue("workivity-session-display-name");
    try {
      window.localStorage.removeItem("workivity-portal-session");
    } catch (_error) {
      /* a blocked localStorage only costs us this cleanup */
    }
  }

  async function handleDesktopLogout() {
    setDesktopSession(null);
    await clearDesktopSessionLocally();
    // Tell the main process too, or it keeps a refresh token for a session the
    // window has already forgotten.
    try {
      await window.desktopMonitor?.signOut?.();
    } catch (_error) {
      /* best effort */
    }
  }

  useEffect(() => {
    async function loadDesktopSession() {
      if (!isDesktop) {
        return;
      }
      const savedSession = await getDesktopValue("workivity-desktop-session", null);

      // The main process owns the socket, so it is the only thing that knows
      // whether this machine can actually authenticate. A session left behind
      // by v1.0.x looks valid here but has no device tokens, and trusting it
      // would show a signed-in UI that silently sends nothing.
      let mainHasSession = true;
      try {
        const state = await window.desktopMonitor?.getAuthState?.();
        if (state && typeof state.signedIn === "boolean") {
          mainHasSession = state.signedIn;
        }
      } catch (_error) {
        // An older preload without getAuthState: fall back to trusting the
        // stored session rather than locking the employee out.
      }

      if (savedSession && !mainHasSession) {
        await clearDesktopSessionLocally();
        setDesktopSession(null);
        setDesktopReady(true);
        return;
      }

      setDesktopSession(savedSession);
      setDesktopReady(true);
    }
    loadDesktopSession();
  }, [isDesktop]);

  // The main process pushes this when a refresh token is rejected or the
  // account is deactivated, so the window does not sit on a dead session.
  useEffect(() => {
    if (!isDesktop || !window.desktopMonitor?.onForceLogout) {
      return undefined;
    }
    const unsubscribe = window.desktopMonitor.onForceLogout(async () => {
      await clearDesktopSessionLocally();
      setDesktopSession(null);
      setDesktopReady(true);
    });
    return typeof unsubscribe === "function" ? unsubscribe : undefined;
  }, [isDesktop]);

  useEffect(() => {
    async function validateToken() {
      if (!session?.token) {
        return;
      }

      try {
        const response = await fetch(`${API_URL}/api/auth/me`, {
          headers: {
            Authorization: `Bearer ${session.token}`,
          },
        });

        if (!response.ok) {
          handleLogout();
          return;
        }

        const payload = await parseApiJson(response);
        if (payload?.user) {
          const nextSession = { token: session.token, user: payload.user };
          setSession(nextSession);
          localStorage.setItem("workivity-portal-session", JSON.stringify(nextSession));
        }
      } catch (_error) {
        handleLogout();
      }
    }

    validateToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.token]);

  useEffect(() => {
    async function validateDesktopToken() {
      if (!desktopSession?.token) {
        return;
      }

      try {
        const response = await fetch(`${API_URL}/api/auth/me`, {
          headers: {
            Authorization: `Bearer ${desktopSession.token}`,
          },
        });

        if (!response.ok) {
          if (shouldClearPersistedSession(response)) {
            await handleDesktopLogout();
          }
          return;
        }

        const payload = await parseApiJson(response);
        if (payload?.user) {
          const nextDesktopSession = { token: desktopSession.token, user: payload.user };
          setDesktopSession(nextDesktopSession);
          await setDesktopValue("workivity-desktop-session", nextDesktopSession);
        }
      } catch (_error) {
        // Keep the saved desktop session when the bundled backend is still starting
        // or the laptop is temporarily offline. A real sign-out still clears it.
      }
    }

    validateDesktopToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [desktopSession?.token]);

  useEffect(() => {
    async function notifyForRestoredDesktopSession() {
      if (!isDesktop || !desktopSession?.token) {
        return;
      }
      if (notifiedDesktopTokenRef.current === desktopSession.token) {
        return;
      }
      notifiedDesktopTokenRef.current = desktopSession.token;
      await notifyDesktopLogin({ user: desktopSession.user });
    }

    notifyForRestoredDesktopSession();
  }, [desktopSession?.token, desktopSession?.user, isDesktop, notifyDesktopLogin]);

  if (isDesktop) {
    if (!desktopReady) {
      return <main className="login-shell">Loading desktop session...</main>;
    }

    if (!desktopSession?.token) {
      return (
        <ErrorBoundary>
          <DesktopLoginPage onLoginSuccess={handleDesktopLoginSuccess} />
        </ErrorBoundary>
      );
    }

    return (
      <ErrorBoundary>
        <DesktopPage
          onLogout={handleDesktopLogout}
          currentUser={desktopSession.user}
          authToken={desktopSession.token}
        />
      </ErrorBoundary>
    );
  }

  return (
    <BrowserRouter>
      {/* Catches a failed lazy chunk after a deploy, which would otherwise
          unmount the whole tree and leave a blank page. */}
      <ErrorBoundary>
      <Suspense fallback={<div className="app-loading"><div className="app-loading-spinner" /></div>}>
      <Routes>
        <Route
          path="/login"
          element={
            session?.token ? (
              <Navigate
                to={isFullPortalRole(session?.user?.role) ? "/overview" : "/my-activities"}
                replace
              />
            ) : (
              <LoginPage onLoginSuccess={handleLoginSuccess} />
            )
          }
        />
        <Route
          path="/overview"
          element={
            session?.token ? (
              isFullPortalRole(session?.user?.role) ? (
                <DashboardPage authToken={session.token} onLogout={handleLogout} onTokenRefresh={handleSessionTokenRefresh} currentUser={session.user} />
              ) : (
                <EmployeeLimitedAccessPage role={session?.user?.role} onLogout={handleLogout} />
              )
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/team"
          element={
            session?.token ? (
              isFullPortalRole(session?.user?.role) ? (
                <DashboardPage authToken={session.token} onLogout={handleLogout} onTokenRefresh={handleSessionTokenRefresh} currentUser={session.user} />
              ) : (
                <Navigate to="/my-activities" replace />
              )
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/my-activities"
          element={
            session?.token ? (
              isFullPortalRole(session?.user?.role) ? (
                <DashboardPage authToken={session.token} onLogout={handleLogout} onTokenRefresh={handleSessionTokenRefresh} currentUser={session.user} />
              ) : (
                <DashboardPage
                  employeePortal
                  authToken={session.token}
                  onLogout={handleLogout}
                  currentUser={session.user}
                />
              )
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/timesheet"
          element={
            session?.token ? (
              isFullPortalRole(session?.user?.role) ? (
                <DashboardPage authToken={session.token} onLogout={handleLogout} onTokenRefresh={handleSessionTokenRefresh} currentUser={session.user} />
              ) : (
                <Navigate to="/my-activities" replace />
              )
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/manual-time-edits"
          element={
            session?.token ? (
              isFullPortalRole(session?.user?.role) ? (
                <Navigate to="/overview" replace />
              ) : (
                <Navigate to="/my-activities" replace />
              )
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/review-apps"
          element={
            session?.token ? (
              isFullPortalRole(session?.user?.role) ? (
                <DashboardPage authToken={session.token} onLogout={handleLogout} onTokenRefresh={handleSessionTokenRefresh} currentUser={session.user} />
              ) : (
                <Navigate to="/my-activities" replace />
              )
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/screenshots"
          element={
            session?.token ? (
              isFullPortalRole(session?.user?.role) ? (
                <DashboardPage authToken={session.token} onLogout={handleLogout} onTokenRefresh={handleSessionTokenRefresh} currentUser={session.user} />
              ) : (
                <Navigate to="/my-activities" replace />
              )
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/work-times"
          element={
            session?.token ? (
              isFullPortalRole(session?.user?.role) ? (
                <DashboardPage authToken={session.token} onLogout={handleLogout} onTokenRefresh={handleSessionTokenRefresh} currentUser={session.user} />
              ) : (
                <Navigate to="/my-activities" replace />
              )
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/productivity"
          element={
            session?.token ? (
              isFullPortalRole(session?.user?.role) ? (
                <DashboardPage authToken={session.token} onLogout={handleLogout} onTokenRefresh={handleSessionTokenRefresh} currentUser={session.user} />
              ) : (
                <Navigate to="/my-activities" replace />
              )
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/apps-summary"
          element={
            session?.token ? (
              isFullPortalRole(session?.user?.role) ? (
                <DashboardPage authToken={session.token} onLogout={handleLogout} onTokenRefresh={handleSessionTokenRefresh} currentUser={session.user} />
              ) : (
                <Navigate to="/my-activities" replace />
              )
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/employees"
          element={
            session?.token ? (
              isFullPortalRole(session?.user?.role) ? (
                <DashboardPage authToken={session.token} onLogout={handleLogout} onTokenRefresh={handleSessionTokenRefresh} currentUser={session.user} />
              ) : (
                <Navigate to="/my-activities" replace />
              )
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/assign-managers"
          element={
            session?.token ? (
              isFullPortalRole(session?.user?.role) ? (
                <DashboardPage authToken={session.token} onLogout={handleLogout} onTokenRefresh={handleSessionTokenRefresh} currentUser={session.user} />
              ) : (
                <Navigate to="/my-activities" replace />
              )
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="/affiliate"
          element={<Navigate to="/my-activities" replace />}
        />
        <Route
          path="/settings"
          element={
            session?.token ? (
              isFullPortalRole(session?.user?.role) ? (
                <DashboardPage authToken={session.token} onLogout={handleLogout} onTokenRefresh={handleSessionTokenRefresh} currentUser={session.user} />
              ) : (
                <Navigate to="/my-activities" replace />
              )
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route path="/dashboard/*" element={<Navigate to="/overview" replace />} />
        <Route
          path="*"
          element={
            <Navigate
              replace
              to={
                session?.token
                  ? isFullPortalRole(session?.user?.role)
                    ? "/overview"
                    : "/my-activities"
                  : "/login"
              }
            />
          }
        />
      </Routes>
      </Suspense>
      </ErrorBoundary>
    </BrowserRouter>
  );
}

export default App;
